var _excluded = ["fullWidth", "color", "label", "helperText", "helperTextPlacement", "edges", "variant", "disabled", "required", "options", "onChange", "showError", "errorMessage", "native", "inputRef", "placeholder", "IconComponent", "size", "value", "name", "inputProps", "hideLabel", "allowEmpty", "weight"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React from 'react';
import PropTypes from 'prop-types';
import MuiSelect from '@material-ui/core/Select';
import MuiMenuItem from '@material-ui/core/MenuItem';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import FormControl from '../FormControl';
import useStyles from './Select.styled';
function Select(_ref) {
  var fullWidth = _ref.fullWidth,
    color = _ref.color,
    label = _ref.label,
    helperText = _ref.helperText,
    helperTextPlacement = _ref.helperTextPlacement,
    edges = _ref.edges,
    variant = _ref.variant,
    disabled = _ref.disabled,
    required = _ref.required,
    options = _ref.options,
    onChange = _ref.onChange,
    showError = _ref.showError,
    errorMessage = _ref.errorMessage,
    native = _ref.native,
    inputRef = _ref.inputRef,
    placeholder = _ref.placeholder,
    IconComponent = _ref.IconComponent,
    size = _ref.size,
    value = _ref.value,
    name = _ref.name,
    inputProps = _ref.inputProps,
    hideLabel = _ref.hideLabel,
    allowEmpty = _ref.allowEmpty,
    weight = _ref.weight,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  var classes = useStyles({
    size: size,
    edges: edges,
    variant: variant,
    required: required,
    native: native,
    placeholder: placeholder,
    value: value,
    color: color,
    weight: weight
  });
  return /*#__PURE__*/React.createElement(FormControl, {
    color: color,
    fullWidth: fullWidth,
    label: label,
    name: name,
    helperText: helperText,
    helperTextPlacement: helperTextPlacement,
    disabled: disabled,
    required: required,
    showError: showError,
    errorMessage: errorMessage,
    edges: edges,
    hideLabel: hideLabel
  }, /*#__PURE__*/React.createElement("div", {
    className: native && placeholder && value === '' && !allowEmpty ? classes.nativePlaceholder : null
  }, /*#__PURE__*/React.createElement(MuiSelect, _extends({
    name: name,
    className: classes.select,
    variant: variant,
    disabled: disabled,
    fullWidth: fullWidth,
    displayEmpty: !!placeholder || allowEmpty,
    error: !!(errorMessage && showError),
    native: native,
    inputProps: _extends({}, inputProps, {
      ref: inputRef
    }),
    MenuProps: _extends({
      style: {
        maxHeight: 500,
        maxWidth: 250
      },
      getContentAnchorEl: null,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left'
      }
    }, !!rest.MenuProps && _extends({}, rest.MenuProps)),
    renderValue: function renderValue(selected) {
      if (selected === '') {
        return /*#__PURE__*/React.createElement("span", {
          className: classes.placeholder
        }, placeholder);
      }
      var findLabel = options.find(function (option) {
        return option.value === selected;
      });
      return findLabel ? findLabel.label : '';
    },
    IconComponent: IconComponent === undefined ? KeyboardArrowDownIcon : IconComponent,
    value: value,
    onChange: onChange
  }, rest), function () {
    if (native) {
      return /*#__PURE__*/React.createElement("option", {
        hidden: !allowEmpty,
        disabled: !allowEmpty
      }, allowEmpty ? placeholder : '');
    }
    if (allowEmpty) {
      return /*#__PURE__*/React.createElement(MuiMenuItem, {
        selected: !value,
        className: classes.placeholder
      }, placeholder);
    }
    return null;
  }(), options && options.map(function (option) {
    return native ? /*#__PURE__*/React.createElement("option", {
      key: option.value,
      value: option.value,
      disabled: option.disabled
    }, option.label) : /*#__PURE__*/React.createElement(MuiMenuItem, {
      key: option.value,
      value: option.value,
      disabled: option.disabled,
      "aria-selected": option.focused,
      "data-focus": option.focused
    }, option.label);
  }))));
}
Select.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * If `true`, the `input` element will be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Define color
   */
  color: PropTypes.oneOf(['default', 'inherit']),
  /**
   * Make button edges rounded or square
   */
  edges: PropTypes.oneOf(['normal', 'rounded', 'sharp']),
  /**
   * If `true`, the input will take up the full width of its container.
   */
  fullWidth: PropTypes.bool,
  /**
   * The helper text content.
   */
  helperText: PropTypes.string,
  /**
   * Placed the helper text above or below the input.
   */
  helperTextPlacement: PropTypes.oneOf(['above', 'below']),
  /**
   * The icon that displays the arrow.
   */
  IconComponent: PropTypes.any,
  /**
   * The label content.
   */
  label: PropTypes.string,
  /**
   * Make label visible only to screen readers
   */
  hideLabel: PropTypes.bool,
  /**
   * The label content.
   */
  name: PropTypes.string.isRequired,
  /**
   * The short hint displayed in the input before the user enters a value.
   */
  placeholder: PropTypes.string,
  /**
   * If `true`, the label is displayed as required and the `input` element` will be required.
   */
  required: PropTypes.bool,
  /**
   * If `true`, the component will render a native <select> element
   */
  native: PropTypes.bool,
  /**
   * The options for this field
   */
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    disabled: PropTypes.bool,
    focused: PropTypes.bool
  })).isRequired,
  /**
   * The size to use.
   */
  size: PropTypes.oneOf(['medium', 'small', 'xsmall']),
  /**
   * The variant to use.
   */
  variant: PropTypes.oneOf(['filled', 'outlined', 'standard']),
  /**
   * @ignore
   */
  value: PropTypes.any,
  /**
   * Whether or not to display an error message (if there is a message to display).
   */
  showError: PropTypes.bool,
  /**
   * The error message to display if showError === true
   */
  errorMessage: PropTypes.string,
  /**
   * @ignore
   */
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  })]),
  /**
   * Attributes applied to the input element. When native is true, the attributes are applied on the select element
   */
  inputProps: PropTypes.object,
  /**
   * Allow empty value
   */
  allowEmpty: PropTypes.bool,
  /**
   * @ignore
   */
  onChange: PropTypes.func,
  /**
   *
   */
  weight: PropTypes.string
} : {};
Select.defaultProps = {
  disabled: false,
  color: 'default',
  edges: 'normal',
  fullWidth: true,
  helperText: '',
  helperTextPlacement: 'above',
  IconComponent: undefined,
  label: '',
  hideLabel: false,
  native: false,
  placeholder: '',
  required: false,
  size: 'medium',
  value: '',
  variant: 'outlined',
  showError: false,
  errorMessage: '',
  inputRef: null,
  inputProps: {},
  allowEmpty: false,
  onChange: function onChange() {
    return null;
  },
  weight: '500'
};
export default Select;